import { useState, useEffect } from 'react';
import { usePersistedState } from '../../hooks/usePersistedState';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { MultiSelectFilter } from '../../components/ui/multi-select-filter';
import {
  Search, Ticket, AlertTriangle, Clock, ChevronRight, Building2, Crown, Star, ArrowUpDown, ArrowUp, ArrowDown, User, ArrowUpCircle, RefreshCw, MessageSquarePlus, ExternalLink, RotateCcw
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const TicketsList = () => {
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const adminId = admin?.id || 'sa';
  const [search, setSearch] = usePersistedState('sa_tickets_search', '', adminId);
  const [statusFilter, setStatusFilter] = usePersistedState('sa_tickets_status', ['active'], adminId);
  const [priorityFilter, setPriorityFilter] = usePersistedState('sa_tickets_priority', [], adminId);
  const [tierFilter, setTierFilter] = usePersistedState('sa_tickets_tier', [], adminId);
  const [assigneeFilter, setAssigneeFilter] = usePersistedState('sa_tickets_assignee', [], adminId);
  const [sourceFilter, setSourceFilter] = usePersistedState('sa_tickets_source', [], adminId);
  const [sortField, setSortField] = usePersistedState('sa_tickets_sort_field', 'updated_at', adminId);
  const [sortDirection, setSortDirection] = usePersistedState('sa_tickets_sort_dir', 'desc', adminId);
  const [refreshing, setRefreshing] = useState(false);
  const [jiraStatuses, setJiraStatuses] = useState({});

  // Migrate legacy single-string filter values to arrays on mount
  if (typeof statusFilter === 'string') setStatusFilter(statusFilter === 'all' ? [] : [statusFilter]);
  if (typeof priorityFilter === 'string') setPriorityFilter(priorityFilter === 'all' ? [] : [priorityFilter]);
  if (typeof tierFilter === 'string') setTierFilter(tierFilter === 'all' ? [] : [tierFilter]);
  if (typeof assigneeFilter === 'string') setAssigneeFilter(assigneeFilter === 'all' ? [] : [assigneeFilter]);
  if (typeof sourceFilter === 'string') setSourceFilter(sourceFilter === 'all' ? [] : [sourceFilter]);

  const FILTER_DEFAULTS = { search: '', statusFilter: ['active'], priorityFilter: [], tierFilter: [], assigneeFilter: [], sourceFilter: [], sortField: 'updated_at', sortDirection: 'desc' };
  const arrEq = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
  const isFiltersModified = search !== FILTER_DEFAULTS.search || !arrEq(statusFilter, FILTER_DEFAULTS.statusFilter) || !arrEq(priorityFilter, FILTER_DEFAULTS.priorityFilter) || !arrEq(tierFilter, FILTER_DEFAULTS.tierFilter) || !arrEq(assigneeFilter, FILTER_DEFAULTS.assigneeFilter) || !arrEq(sourceFilter, FILTER_DEFAULTS.sourceFilter) || sortField !== FILTER_DEFAULTS.sortField || sortDirection !== FILTER_DEFAULTS.sortDirection;

  const handleResetFilters = () => {
    setSearch(FILTER_DEFAULTS.search);
    setStatusFilter([...FILTER_DEFAULTS.statusFilter]);
    setPriorityFilter([...FILTER_DEFAULTS.priorityFilter]);
    setTierFilter([...FILTER_DEFAULTS.tierFilter]);
    setAssigneeFilter([...FILTER_DEFAULTS.assigneeFilter]);
    setSourceFilter([...FILTER_DEFAULTS.sourceFilter]);
    setSortField(FILTER_DEFAULTS.sortField);
    setSortDirection(FILTER_DEFAULTS.sortDirection);
  };

  const fetchTickets = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get('/api/super-admin/tickets');
      setTickets(res.data);
      fetchJiraStatuses(res.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchJiraStatuses = async (ticketList) => {
    const links = [...new Set((ticketList || []).map(t => t.jira_work_item).filter(Boolean))];
    if (links.length === 0) return;
    try {
      const res = await api.post('/api/super-admin/tickets/jira-status', { links });
      setJiraStatuses(res.data.statuses || {});
    } catch (err) {
      console.error('Failed to fetch JIRA statuses:', err);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const filteredTickets = tickets.filter(t => {
    const matchesSearch =
      t.subject?.toLowerCase().includes(search.toLowerCase()) ||
      t.ticket_number?.toLowerCase().includes(search.toLowerCase()) ||
      t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      t.jira_work_item?.toLowerCase().includes(search.toLowerCase());
    
    // Status filter — empty array = all; ['active'] = hide closed/resolved; otherwise match any selected
    let matchesStatus = true;
    if (statusFilter.length > 0) {
      if (statusFilter.includes('active')) {
        // 'active' hides closed and resolved; also allow any other explicitly selected statuses
        const extras = statusFilter.filter(s => s !== 'active');
        matchesStatus =
          (t.status !== 'closed' && t.status !== 'resolved') ||
          extras.includes(t.status);
      } else {
        matchesStatus = statusFilter.includes(t.status);
      }
    }
    
    const matchesPriority = priorityFilter.length === 0 || priorityFilter.includes(t.priority);
    const matchesTier = tierFilter.length === 0 || tierFilter.includes(t.tenant_tier);
    
    // Assignee filter — empty = all; supports multi-select of 'mine' and 'unassigned'
    let matchesAssignee = true;
    if (assigneeFilter.length > 0) {
      matchesAssignee = assigneeFilter.some(af => {
        if (af === 'mine') return t.assigned_to_id === admin?.id;
        if (af === 'unassigned') return !t.assigned_to_id;
        return false;
      });
    }

    // Source filter
    const matchesSource = sourceFilter.length === 0 || sourceFilter.includes(t.source || 'tenant');

    return matchesSearch && matchesStatus && matchesPriority && matchesTier && matchesAssignee && matchesSource;
  });

  // Sort tickets
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    let aValue, bValue;
    
    if (sortField === 'updated_at') {
      aValue = new Date(a.updated_at || a.created_at);
      bValue = new Date(b.updated_at || b.created_at);
    } else if (sortField === 'created_at') {
      aValue = new Date(a.created_at);
      bValue = new Date(b.created_at);
    } else {
      return 0;
    }
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-400';
      case 'open': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-400';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      case 'closed': return 'bg-slate-500/20 text-slate-400';
      case 'reopened_by_customer': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-red-400" />;
      case 'high': return <Clock className="h-4 w-4 text-orange-400" />;
      case 'medium': return <Clock className="h-4 w-4 text-yellow-400" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'professional':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            <Crown className="h-3 w-3 mr-1" />
            Pro
          </Badge>
        );
      case 'advanced':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Star className="h-3 w-3 mr-1" />
            Adv
          </Badge>
        );
      default:
        return null; // No badge for basic tier
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  const openCount = tickets.filter(t => ['open', 'in_progress', 'waiting_on_customer', 'waiting_on_support', 'reopened_by_customer', 'new'].includes(t.status)).length;
  const closedCount = tickets.filter(t => t.status === 'closed' || t.status === 'resolved').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Tickets</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {openCount} open tickets
            {closedCount > 0 && statusFilter.includes('active') && <span className="ml-2">• {closedCount} closed/resolved (hidden)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => fetchTickets({ silent: true })}
            disabled={refreshing}
            data-testid="tickets-refresh-btn"
            className={isDark ? 'bg-slate-700/50 border-slate-600 text-white hover:bg-slate-700' : ''}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              <Input
                placeholder="Search by subject, ticket # or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              />
            </div>
            <MultiSelectFilter
              label="Status: Active"
              options={[
                { value: 'active', label: 'Active (excl. Resolved/Closed)' },
                { value: 'new', label: 'New' },
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'waiting_on_customer', label: 'Waiting on Customer' },
                { value: 'waiting_on_support', label: 'Waiting on Support' },
                { value: 'reopened_by_customer', label: 'Reopened by Customer' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'closed', label: 'Closed' },
              ]}
              selected={statusFilter}
              onSelectionChange={setStatusFilter}
              testId="tickets-status-filter"
              className={`w-full sm:w-44 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
            />
            <MultiSelectFilter
              label="All Priority"
              options={[
                { value: 'urgent', label: 'Urgent' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
              selected={priorityFilter}
              onSelectionChange={setPriorityFilter}
              testId="tickets-priority-filter"
              className={`w-full sm:w-36 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
            />
            <MultiSelectFilter
              label="All Tiers"
              options={[
                { value: 'professional', label: 'Professional' },
                { value: 'advanced', label: 'Advanced' },
                { value: 'basic', label: 'Basic' },
              ]}
              selected={tierFilter}
              onSelectionChange={setTierFilter}
              testId="tickets-tier-filter"
              className={`w-full sm:w-36 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
            />
            <MultiSelectFilter
              label="All Tickets"
              options={[
                { value: 'mine', label: 'Assigned to Me' },
                { value: 'unassigned', label: 'Unassigned' },
              ]}
              selected={assigneeFilter}
              onSelectionChange={setAssigneeFilter}
              testId="tickets-assignee-filter"
              className={`w-full sm:w-40 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
            />
            <MultiSelectFilter
              label="All Sources"
              options={[
                { value: 'tenant', label: 'Support Tickets' },
                { value: 'ea_feedback', label: 'EA Feedback' },
              ]}
              selected={sourceFilter}
              onSelectionChange={setSourceFilter}
              testId="tickets-source-filter"
              className={`w-full sm:w-40 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
            />
            {isFiltersModified && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="reset-filters-btn"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          {sortedTickets.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className={`h-12 w-12 mx-auto ${isDark ? 'text-slate-500' : 'text-gray-400'} mb-4`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No tickets found</p>
              {statusFilter.includes('active') && closedCount > 0 && (
                <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {closedCount} closed/resolved ticket{closedCount !== 1 ? 's' : ''} hidden.{' '}
                  <button 
                    onClick={() => setStatusFilter([])} 
                    className="text-[#ffd108] hover:underline"
                  >
                    Show all
                  </button>
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Subject</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Company</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Assignee</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>JIRA</TableHead>
                  <TableHead 
                    className={`${isDark ? 'text-slate-400' : 'text-gray-500'} cursor-pointer hover:text-[#ffd108] select-none`}
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Created
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={`${isDark ? 'text-slate-400' : 'text-gray-500'} cursor-pointer hover:text-[#ffd108] select-none`}
                    onClick={() => toggleSort('updated_at')}
                  >
                    <div className="flex items-center">
                      Last Updated
                      {getSortIcon('updated_at')}
                    </div>
                  </TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className={`${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                    onClick={() => navigate(`/super-admin/tickets/${ticket.id}`)}
                  >
                    <TableCell className="text-[#ffd108] font-mono text-sm">
                      {ticket.ticket_number}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</p>
                          {ticket.source === 'ea_feedback' && (
                            <Badge data-testid={`ticket-ea-badge-${ticket.id}`} className="bg-[#ffd108]/15 text-[#ffd108] border-[#ffd108]/30 text-[10px] px-1.5 py-0 whitespace-nowrap">
                              <MessageSquarePlus className="h-3 w-3 mr-0.5" />
                              EA Feedback
                            </Badge>
                          )}
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'} capitalize`}>{ticket.category?.replace(/_/g, ' ')}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                        <span className={isDark ? 'text-slate-300' : 'text-gray-600'}>{ticket.company_name}</span>
                        {getTierBadge(ticket.tenant_tier)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge className={getStatusColor(ticket.status)}>
                          {ticket.status?.replace(/_/g, ' ')}
                        </Badge>
                        {ticket.is_escalated && (
                          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px] px-1.5 py-0">
                            <ArrowUpCircle className="h-3 w-3 mr-0.5" />
                            Escalated
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getPriorityIcon(ticket.priority)}
                        <span className={`${isDark ? 'text-slate-300' : 'text-gray-600'} capitalize`}>{ticket.priority}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {ticket.assigned_to_name ? (
                        <div className="flex items-center gap-2">
                          <User className={`h-4 w-4 ${ticket.assigned_to_id === admin?.id ? 'text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                          <span className={ticket.assigned_to_id === admin?.id ? 'text-[#ffd108] font-medium' : isDark ? 'text-slate-300' : 'text-gray-600'}>
                            {ticket.assigned_to_id === admin?.id ? 'Me' : ticket.assigned_to_name}
                          </span>
                        </div>
                      ) : (
                        <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`ticket-jira-${ticket.id}`}>
                      {ticket.jira_work_item ? (
                        (() => {
                          const js = jiraStatuses[ticket.jira_work_item];
                          const isUrl = /^https?:\/\//i.test(ticket.jira_work_item);
                          const keyLabel = js?.issue_key || ticket.jira_work_item;
                          const badge = js?.ok ? (
                            <Badge className={
                              js.category === 'done'
                                ? 'bg-green-500/20 text-green-500 border border-green-500/30 text-[10px] px-1.5 py-0'
                                : js.category === 'in_progress'
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[10px] px-1.5 py-0'
                                : 'bg-slate-500/20 text-slate-400 border border-slate-500/30 text-[10px] px-1.5 py-0'
                            }>
                              {js.status_name}
                            </Badge>
                          ) : null;
                          const linkEl = (isUrl || js?.url) ? (
                            <a
                              href={js?.url || ticket.jira_work_item}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[#ffd108] hover:underline font-mono text-xs max-w-[120px] truncate"
                              title={ticket.jira_work_item}
                            >
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{keyLabel}</span>
                            </a>
                          ) : (
                            <span className={`font-mono text-xs ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{keyLabel}</span>
                          );
                          const typeEl = js?.ok && js.issue_type ? (
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                              title={`Type: ${js.issue_type}`}
                            >
                              {js.issue_type_icon && (
                                <img src={js.issue_type_icon} alt="" className="h-3.5 w-3.5 flex-shrink-0" />
                              )}
                              <span className="whitespace-nowrap">{js.issue_type}</span>
                            </span>
                          ) : null;
                          return (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {linkEl}
                              {typeEl}
                              {badge}
                            </div>
                          );
                        })()
                      ) : (
                        <span className={isDark ? 'text-slate-600' : 'text-gray-300'}>—</span>
                      )}
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {ticket.updated_at 
                        ? formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketsList;
